import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/primitives/dialog";
import {
  TransformWrapper,
  TransformComponent,
  type ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { useSwipeable } from "react-swipeable";
import { AlertCircle, Pause, Play, Volume2, VolumeX } from "lucide-react";
import ViewerControls from "./ViewerControls";
import VideoScrubBar from "./VideoScrubBar";
import UnplayableVideoCard from "./UnplayableVideoCard";
import { useVideoPlayback } from "./useVideoPlayback";
import { useAutoHideControls } from "./useAutoHideControls";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useMediaAttachment from "@/middleware/hooks/attachments/useMediaAttachment";
import useLargeVideoAttachment from "@/middleware/hooks/attachments/useLargeVideoAttachment";
import LoadingScreen from "@/components/shared/LoadingScreen";
import VideoViewer from "./VideoViewer";
import StreamedVideoViewer from "./StreamedVideoViewer";
import useFileAttachment from "@/middleware/hooks/attachments/useFileAttachment";
import useBlobToImageURL from "@/hooks/useBlobToImageURL";
import { featureFlags } from "@/config/featureFlags";
import { useAttachmentViewerState } from "@/zustand/AttachmentViewerState";
import { useNavigationState } from "@/zustand/NavigationState";
import { useUploadProgressState } from "@/zustand/UploadProgressState";
import { useUploadControlState } from "@/zustand/UploadControlState";
import { useSafeLiveQuery } from "@/indexedDB/hooks/useSafeLiveQuery";
import { getMediaAttachmentContentFromLocalDB } from "@/indexedDB/services/attachments";
import { getChatFromLocalDB } from "@/indexedDB/services/chats";
import { getViewerMediaAttachments } from "@/indexedDB/services/messages";
import { placeholderToDataURL } from "@/utils/mediaPlaceholder";
import { cn } from "@/lib/utils";
import ViewerDownloadProgress from "./ViewerDownloadProgress";
import { mediaCacheKey, peekCachedMedia } from "@/middleware/mediaMemoryCache";
import { isImageType, isVideoType } from "@/utils/attachmentType";
import { useCloseViewerOnMessageDeleted } from "./useCloseViewerOnMessageDeleted";
import ViewerImage from "./ViewerImage";
import useViewerKeyboardNav from "./useViewerKeyboardNav";
import useViewerPrefetch from "./useViewerPrefetch";
import { useViewerHistorySentinel } from "./useViewerHistorySentinel";
import {
  isDecisiveHorizontalSwipe,
  isDecisiveVerticalSwipe,
  startedOnInteractive,
} from "./swipeGuards";

const PdfViewer = lazy(() => import("./PdfViewer"));
const PdfNavigation = lazy(() => import("./PdfNavigation"));

const AttachmentViewerDialog = () => {
  const open = useAttachmentViewerState((state) => state.open);
  const threadId = useAttachmentViewerState((state) => state.threadId);
  const closeDialog = useAttachmentViewerState((state) => state.closeDialog);

  const attachment = useAttachmentViewerState((state) => state.attachment);

  const setAttachments = useAttachmentViewerState(
    (state) => state.setAttachments,
  );
  const index = useAttachmentViewerState((state) => state.index);
  const attachments = useAttachmentViewerState((state) => state.attachments);
  const showNext = useAttachmentViewerState((state) => state.showNext);
  const showPrevious = useAttachmentViewerState((state) => state.showPrevious);
  const nextAttachment = useAttachmentViewerState(
    (state) => state.nextAttachment,
  );
  const previousAttachment = useAttachmentViewerState(
    (state) => state.previousAttachment,
  );
  // Navigation list = images + videos from the same context (main chat or the
  // thread the viewer was opened in), sourced from the messages table. Scoping
  // by threadId is what stops thread media and main-chat media leaking into
  // each other's carousel.
  const mediaAttachments = useSafeLiveQuery(
    () => getViewerMediaAttachments(attachment.chatId || "", threadId),
    [attachment.chatId, threadId],
  );

  const activeChatId = useNavigationState((state) => state.activeChatId);
  // eCSK of the chat the ATTACHMENT belongs to — deliberately NOT the active
  // chat's key. A notification tap can switch chats while a streamed video is
  // open; keying on activeChatECSK would re-register the stream with the NEW
  // chat's key and fail decryption in the SW. (The close-on-chat-change effect
  // below also closes the viewer, but the key must never be wrong even for a
  // frame.)
  const chatECSK =
    useSafeLiveQuery(
      () =>
        attachment.chatId
          ? getChatFromLocalDB(attachment.chatId).then((c) => c?.eCSK ?? "")
          : Promise.resolve(""),
      [attachment.chatId],
    ) ?? "";

  // Close when the active chat changes away from — or clears from under — the
  // attachment's chat: a notification tap switches chats with the viewer open,
  // and a remote removal (kicked from the group, chat pruned) unmounts
  // MiddleColumn entirely while `open` would otherwise stay true in the store,
  // making the viewer "reopen" by itself over the NEXT chat.
  useEffect(() => {
    if (open && attachment.chatId && activeChatId !== attachment.chatId) {
      closeDialog();
    }
  }, [open, attachment.chatId, activeChatId, closeDialog]);

  const uploadState = useAttachmentViewerState((state) => state.uploadState);
  const fileSize = useAttachmentViewerState((state) => state.fileSize);

  const isImage = isImageType(attachment.fileType);
  const isVideo = isVideoType(attachment.fileType);
  const isPdf: boolean = attachment.fileType === "application/pdf";

  const isLargeVideo: boolean =
    featureFlags.chunkedUpload &&
    isVideo &&
    !!attachment.isLargeAttachment &&
    !!attachment.id;

  // Images and small videos load their full bytes through useMediaAttachment;
  // large videos stream, PDFs use their own loader.
  const mediaCanLoad = (isImage || (isVideo && !isLargeVideo)) && open;

  const [videoElementError, setVideoElementError] = useState(false);
  // MediaError.code from the <video> element (1 aborted / 2 network /
  // 3 decode / 4 src-not-supported) — routes the small-video error card and
  // shows in the generic error suffix.
  const [videoElementErrorCode, setVideoElementErrorCode] = useState<
    number | null
  >(null);
  // The small video stays INVISIBLE until it knows its real size (VideoViewer's
  // onReady): before loadedmetadata its intrinsic size is the poster's, so the
  // element painted a thumbnail-sized video that then snapped to full size. The
  // timeout is the escape hatch for the reachable "no metadata AND no
  // MediaError" state (a skipped <source> swallows its error event by design):
  // after 2s we reveal whatever the element has rather than hiding it forever.
  const [videoReady, setVideoReady] = useState(false);
  // Render-phase reset (React's "adjust state when props change"): the reset
  // must land in the SAME commit as the new item, or the first frame renders
  // the PREVIOUS video fully visible — or its error card — under the new
  // item's chrome (the old post-commit effect reset allowed exactly that
  // one-frame flash).
  const [videoReadyForId, setVideoReadyForId] = useState(attachment.id);
  if (videoReadyForId !== attachment.id) {
    setVideoReadyForId(attachment.id);
    setVideoReady(false);
    setVideoElementError(false);
    setVideoElementErrorCode(null);
  }

  // The mounted <video> element (small or streamed), handed up by the player
  // components so the CUSTOM controls — which live outside the zoom transform
  // and therefore never scale or collide with iOS's native glass overlay —
  // can drive it. Null while buffering / for non-video items.
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const playback = useVideoPlayback(videoEl);
  // Immersive show/hide: hidden while an autoplaying video plays, tap toggles,
  // auto-hides after 5 s, pinned visible while paused.
  const {
    visible: videoControlsVisible,
    toggle: toggleVideoControls,
    poke: pokeVideoControls,
  } = useAutoHideControls({
    active: isVideo && open,
    isPlaying: playback.isPlaying,
    itemKey: attachment.id,
  });

  const [page, setPage] = useState(1);
  const [pageNumber, setPageNumber] = useState(0);

  // The persistent controls bar reserves space at the bottom: we measure its
  // height and inset the media by that much, so the media fits ABOVE the bar at
  // scale 1 (the stage stays full-height, so a zoomed image still extends
  // behind it). A callback ref measures the bar when it mounts (a plain effect
  // would miss it mounting only after a buffering→loaded transition) and a
  // ResizeObserver re-measures on resize / mobile safe-area change. setState
  // bails when the height is unchanged, so there's no update loop.
  const controlsObserverRef = useRef<ResizeObserver | null>(null);
  const [controlsHeight, setControlsHeight] = useState(0);
  const controlsRef = useCallback((node: HTMLElement | null) => {
    controlsObserverRef.current?.disconnect();
    controlsObserverRef.current = null;
    if (!node) {
      return;
    }
    setControlsHeight(node.offsetHeight);
    controlsObserverRef.current = new ResizeObserver(() =>
      setControlsHeight(node.offsetHeight),
    );
    controlsObserverRef.current.observe(node);
  }, []);

  const data = useMemo(() => {
    const { messageId, timestamp: messageTimestamp, chatId } = attachment;
    const attachmentMeta = {
      attachmentId: attachment.id,
      attachmentThumbnailId: "",
      filename: attachment.filename,
      fileType: attachment.fileType,
      fileSize: fileSize ?? 0,
      isLargeAttachment: attachment.isLargeAttachment,
      uploadState,
    };
    return {
      messageId,
      messageTimestamp,
      chatId,
      attachment: attachmentMeta,
    };
  }, [attachment, uploadState, fileSize]);

  // Per-item state: the retry trigger for a failed full-size load (see the
  // "unavailable" branch) and the PDF page position. Reset on item change so a
  // fresh item never inherits a stale retry — or PDF B opening on PDF A's page
  // index ("8 / 3" on a 3-page document).
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    setReloadKey(0);
    setPage(1);
    setPageNumber(0);
  }, [attachment.id]);

  const { loading: loadingMedia, attachmentUrl: mediaUrl } = useMediaAttachment(
    {
      ...data,
      getThumbnail: false,
      canExecute: mediaCanLoad,
      reloadKey,
    },
  );

  // Thumbnail-first: read the already-cached thumbnail (the same one the bubble
  // rendered) straight from IndexedDB, so the viewer shows a blurred preview
  // instantly instead of a blank spinner while the full-size downloads and
  // decrypts. Deliberately decoupled from the nav-list query. Resolves to null
  // — and the UI falls back to the spinner — when the item's bubble was never
  // on screen (e.g. an item swiped to that was never rendered).
  // The bubble/media-tab already decrypted this thumbnail into the memory cache,
  // so peek it SYNCHRONOUSLY: the IndexedDB read below is async, which left
  // thumbnailUrl === "" on the first render of every open and every swipe — the
  // preview flashed, and a video mounted with no poster at all. Same
  // render-phase adjustment as useMediaAttachment: this dialog is mounted
  // persistently, so a useState initialiser alone would never re-run.
  const thumbKey = mediaCacheKey(attachment.id ?? "", "thumbnail");
  const cachedThumb =
    open && (isImage || isVideo) ? peekCachedMedia(thumbKey) : null;
  const [thumbnailBlob, setThumbnailBlob] = useState<Blob | null>(cachedThumb);
  const [thumbRenderedKey, setThumbRenderedKey] = useState(thumbKey);
  if (thumbRenderedKey !== thumbKey) {
    setThumbRenderedKey(thumbKey);
    setThumbnailBlob(cachedThumb);
  }
  useEffect(() => {
    let cancelled = false;
    // Only blank it on a MISS — blanking a hit would reintroduce the flash.
    if (!peekCachedMedia(thumbKey)) {
      setThumbnailBlob(null);
    }
    if (open && attachment.id && (isImage || isVideo)) {
      getMediaAttachmentContentFromLocalDB(attachment.id, "thumbnail")
        .then((blob) => {
          // Publish only a real blob: a non-null IDB read must still replace a
          // possibly-dead memory-cached handle, but a null result (row missing
          // — private tab, quota-pressed write) must not downgrade a live
          // memory-cached poster to the ThumbHash blur. A genuine miss was
          // already blanked by the peek guard above.
          if (!cancelled && blob) {
            setThumbnailBlob(blob);
          }
        })
        .catch(() => {
          /* no cached thumbnail — the UI falls back to the spinner */
        });
    }
    return () => {
      cancelled = true;
    };
  }, [open, attachment.id, isImage, isVideo, thumbKey]);
  const thumbnailUrl = useBlobToImageURL(thumbnailBlob);

  // Instant ThumbHash blur from the message meta — the fallback beneath the
  // cached thumbnail, so an item swiped to whose bubble never rendered still
  // shows a blur instead of a bare spinner.
  const placeholderUrl = useMemo(
    () => placeholderToDataURL(attachment.placeholder),
    [attachment.placeholder],
  );

  // Warm the ±1 neighbours' cache so navigating to them is instant, and wire
  // arrow-key navigation. Both extracted (and unit-tested) — see the hooks.
  useViewerPrefetch({ enabled: open, index, attachments });
  useViewerKeyboardNav({
    enabled: open,
    showNext,
    showPrevious,
    onNext: nextAttachment,
    onPrevious: previousAttachment,
  });
  // Platform back (Android system Back, iOS edge-swipe) closes the viewer
  // instead of leaving the chat; user closes (X/Esc/backdrop) go through
  // requestClose so they consume the sentinel history entry. See the hook.
  const requestClose = useViewerHistorySentinel(open, closeDialog);

  // Touch-swipe navigation. Gated on zoom: at scale 1 a horizontal drag is a
  // swipe (nav); once zoomed in, the same drag is a pan (react-zoom-pan-pinch),
  // so we disable panning while at scale 1 and disable swipe while zoomed —
  // the two gestures never contend. Two further guards (see swipeGuards.ts):
  // a drag that STARTS on an interactive element (a video's native timeline/
  // volume, the bottom toolbar) belongs to that element — scrubbing must never
  // navigate — and only a decisive horizontal swipe counts, so a drag pulled
  // back to its origin cancels instead of navigating. Handlers read fresh
  // store state (getState) so they can't go stale.
  const [isZoomed, setIsZoomed] = useState(false);
  const isZoomedRef = useRef(false);
  // Captured from the zoom wrapper's onInit so Esc / orientation-change can
  // reset the transform imperatively (the wrapper remounts, so this refreshes).
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const handleTransformed = useCallback(
    (_ref: unknown, state: { scale: number }) => {
      const zoomed = state.scale > 1.01;
      isZoomedRef.current = zoomed;
      setIsZoomed((prev) => (prev === zoomed ? prev : zoomed));
    },
    [],
  );
  // The dialog CONTENT unmounts on close while this component (and the zoom
  // flags) stays mounted — and react-zoom-pan-pinch only reports transforms it
  // performs itself, so closing while zoomed would leave isZoomed stuck true:
  // swipe dead and panning hijacking drags at scale 1 on the next open. Reset
  // on close; onInit (on the wrapper) re-syncs from a fresh mount.
  useEffect(() => {
    if (!open) {
      isZoomedRef.current = false;
      setIsZoomed(false);
    }
  }, [open]);
  // Reset zoom/pan on orientation change: a transform computed for portrait
  // leaves the image off-centre / partly off-screen after rotating to
  // landscape (and vice-versa). Only meaningful while zoomed.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onOrientationChange = () => {
      if (isZoomedRef.current) {
        transformRef.current?.resetTransform();
      }
    };
    window.addEventListener("orientationchange", onOrientationChange);
    return () =>
      window.removeEventListener("orientationchange", onOrientationChange);
  }, [open]);
  // Opening the viewer from a focused composer input leaves the iOS software
  // keyboard up, shoving the fixed-position viewer up by the keyboard height.
  // Blur the active editable element so the keyboard dismisses as it opens.
  useEffect(() => {
    if (!open) {
      return;
    }
    const el = document.activeElement;
    if (
      el instanceof HTMLElement &&
      el.closest("input, textarea, [contenteditable='true']")
    ) {
      el.blur();
    }
  }, [open]);
  const swipeHandlers = useSwipeable({
    onSwipedLeft: (eventData) => {
      if (
        isZoomedRef.current ||
        startedOnInteractive(eventData) ||
        !isDecisiveHorizontalSwipe(eventData)
      ) {
        return;
      }
      const s = useAttachmentViewerState.getState();
      if (s.showNext) {
        s.nextAttachment();
      }
    },
    onSwipedRight: (eventData) => {
      if (
        isZoomedRef.current ||
        startedOnInteractive(eventData) ||
        !isDecisiveHorizontalSwipe(eventData)
      ) {
        return;
      }
      const s = useAttachmentViewerState.getState();
      if (s.showPrevious) {
        s.previousAttachment();
      }
    },
    onSwipedDown: (eventData) => {
      // Swipe-down-to-dismiss — images only (the isImageType gate below).
      // Videos keep the gesture free (tap toggles controls; a stray downward
      // fling shouldn't close a playing video) and PDFs pan vertically at any
      // scale. Gated like the horizontal swipes: not while zoomed, not from an
      // interactive element, and only a decisive vertical drag.
      if (
        isZoomedRef.current ||
        startedOnInteractive(eventData) ||
        !isDecisiveVerticalSwipe(eventData)
      ) {
        return;
      }
      const s = useAttachmentViewerState.getState();
      if (isImageType(s.attachment.fileType)) {
        requestClose();
      }
    },
    trackMouse: false,
    delta: 50,
  });

  const {
    src: largeVideoSrc,
    loading: largeVideoLoading,
    error: largeVideoError,
    streamError: largeVideoStreamError,
    sourceKind: largeVideoSourceKind,
  } = useLargeVideoAttachment(data.attachment, chatECSK, {
    enabled: isLargeVideo && open,
    chatId: data.chatId,
    messageId: data.messageId,
    messageTimestamp: data.messageTimestamp,
  });

  // Diagnostic breadcrumb: which source path is feeding the viewer. Field
  // reports (esp. iOS) need this to tell "in-memory preview" from "local
  // cache" from "SW stream" — they fail in completely different ways.
  useEffect(() => {
    if (open && isLargeVideo) {
      // eslint-disable-next-line no-console
      console.info(
        `[viewer] large-video source=${largeVideoSourceKind} id=${attachment.id}`,
      );
    }
  }, [open, isLargeVideo, largeVideoSourceKind, attachment.id]);

  // Map the SW's failure reason (and the persisted upload status) to a specific
  // message, so a still-uploading video, a failed upload, and a genuine
  // playback error don't all read as the same generic "try again". The
  // generic fallback carries a compact diagnostic suffix (media error code +
  // active source path) so screenshots from testers are actionable.
  const videoErrorMessage =
    largeVideoStreamError === "failed" || uploadState?.status === "failed"
      ? "This video failed to upload. Ask the sender to resend it."
      : largeVideoStreamError === "in-progress"
        ? "This video is still uploading. Check back in a moment."
        : largeVideoStreamError === "unauthorized"
          ? "Your session expired. Reopen the video to try again."
          : `Unable to load video. Please close and try again. [${largeVideoSourceKind}${
              videoElementErrorCode ? ` E${videoElementErrorCode}` : ""
            }]`;

  // Close the viewer if the message being previewed gets deleted — e.g. the
  // sender cancelled a chunked upload we were watching (or their own preview).
  const previewedMessageExists = useCloseViewerOnMessageDeleted(
    attachment.messageId,
    open,
    closeDialog,
  );

  // Receiver-side transfer progress for a watch-while-uploading video, fed by
  // the SW prefetch bridge. The sender plays their local file (their progress
  // entry tracks the upload itself), so the label is receiver-only.
  const receiveProgress = useUploadProgressState((state) =>
    attachment.id ? state.progress[attachment.id] : undefined,
  );
  const isOwnUpload = useUploadControlState((state) =>
    attachment.id ? !!state.controls[attachment.id] : false,
  );
  const bufferingLabel =
    isLargeVideo && !isOwnUpload && receiveProgress !== undefined
      ? `Receiving video… ${receiveProgress}%`
      : undefined;

  const { loading: loadingPdf, attachmentBlob } = useFileAttachment({
    ...data,
    canExecute: isPdf,
  });
  const pdfUrl = useBlobToImageURL(attachmentBlob);

  // A re-registration (e.g. after a mid-stream token refresh) flips the stream
  // back to loading and reloads the <video> (src → null → url). Clear the
  // element-error latch when that happens, so a recovered stream shows the
  // player again instead of staying stuck on the error card (the latch
  // otherwise only clears on an attachment.id change — in the render-phase
  // reset block above).
  useEffect(() => {
    if (largeVideoLoading) {
      setVideoElementError(false);
      setVideoElementErrorCode(null);
    }
  }, [largeVideoLoading]);

  useEffect(() => {
    if (!mediaAttachments) {
      return;
    }
    if (open) {
      setAttachments(mediaAttachments);
    }
  }, [open, mediaAttachments, attachment, setAttachments]);

  const filename = attachment.filename;

  const loading = isLargeVideo
    ? largeVideoLoading
    : isImage || isVideo
      ? loadingMedia
      : loadingPdf;
  const attachmentUrl = isLargeVideo
    ? (largeVideoSrc ?? "")
    : isImage || isVideo
      ? mediaUrl
      : pdfUrl;

  // The full-size fetch finished but produced nothing (failed upload, missing
  // bucket object). Without this the viewer would sit on the blurred
  // placeholder/spinner forever — the nav list now includes never-downloaded
  // items, so a permanently-unfetchable one is reachable. Large video keeps its
  // own richer error path (largeVideoError); this covers image + small video.
  const mediaUnavailable = mediaCanLoad && !loading && attachmentUrl === "";

  // A small (fully-downloaded) video whose <video> element failed to decode/
  // play: the bytes arrived (so mediaUnavailable is false) but playback errored.
  // Surface the same "couldn't be loaded / Retry" card instead of failing mute.
  // Escape hatch for the hidden small video (see the state's comment): if
  // neither loadedmetadata nor canplay ever fires — and no MediaError either —
  // reveal after 2s instead of hiding a possibly-playing video forever.
  useEffect(() => {
    if (!(isVideo && !isLargeVideo) || videoReady || attachmentUrl === "") {
      return;
    }
    const t = setTimeout(() => setVideoReady(true), 2000);
    return () => clearTimeout(t);
  }, [isVideo, isLargeVideo, videoReady, attachmentUrl]);
  const smallVideoError = isVideo && !isLargeVideo && videoElementError;
  // MediaError routing: only decode-class failures — 3 (DECODE) / 4
  // (SRC_NOT_SUPPORTED) — are permanent for these bytes (retrying re-decodes
  // the same data), so only they get the no-retry UnplayableVideoCard. Codes
  // 1 (ABORTED) / 2 (NETWORK) — and a missing code — are transient/dead-blob
  // failures (the row-25/29 class) where a retry re-fetches and self-heals
  // via the liveness guard, so they fall through to the Retry card below.
  const smallVideoUndecodable =
    smallVideoError &&
    (videoElementErrorCode === 3 || videoElementErrorCode === 4);

  const image = (
    <ViewerImage
      fullUrl={attachmentUrl}
      thumbnailUrl={thumbnailUrl}
      placeholderUrl={placeholderUrl}
      bottomInset={controlsHeight}
      naturalWidth={attachment.width}
      naturalHeight={attachment.height}
    />
  );
  // Poster shown while a video buffers AND beneath the still-hidden player: the
  // SAME preview layer the image viewer uses — ViewerImage with fullUrl=""
  // renders only the preview (no full-image swap), so a video shows its cached
  // thumbnail SHARP (fit-to-viewport), a blurred ThumbHash as fallback, else
  // the spinner — consistent with images instead of the old blurred thumbnail.
  // Deliberately NO meta dims and NO spinner here: the poster and the <video>
  // must obey ONE sizing rule — fill the stage, letterboxed — or the reveal
  // jumps (a native-capped poster over a filling video, or the reverse, is
  // exactly the "small thumbnail hop"). Small videos upscale to fit, standard
  // player behavior (images keep their native cap — different medium). The
  // loading indicator belongs to ViewerDownloadProgress alone.
  const videoPoster = (
    <ViewerImage
      fullUrl=""
      thumbnailUrl={thumbnailUrl}
      placeholderUrl={placeholderUrl}
      bottomInset={controlsHeight}
      spinner={false}
    />
  );
  const video = isLargeVideo ? (
    <StreamedVideoViewer
      url={attachmentUrl}
      onError={(mediaError) => {
        // eslint-disable-next-line no-console
        console.error(
          `[viewer] video element error (source=${largeVideoSourceKind}, id=${attachment.id})`,
          mediaError?.code,
          mediaError?.message,
        );
        setVideoElementError(true);
        setVideoElementErrorCode(mediaError?.code ?? null);
      }}
      bufferingLabel={bufferingLabel}
      onVideoElement={setVideoEl}
      onTap={toggleVideoControls}
      // The cached thumbnail when we have it; else the ThumbHash data URL,
      // which is available SYNCHRONOUSLY from the message meta — the thumbnail
      // arrives via an async IndexedDB read, so on the video's mount render it
      // is almost always still "" and the element briefly had no poster at all
      // (a black box until the first frame decoded).
      poster={thumbnailUrl || placeholderUrl || undefined}
    />
  ) : (
    // ONE explicit full-viewport frame owns the geometry for both layers. The
    // <video> is never sized by its own intrinsic size (which is the POSTER's
    // until loadedmetadata — the old "thumbnail-sized video" flash) and, being
    // an explicit box inside the zoom container, it centers properly instead
    // of shrink-wrapping to the screen's top-left. The poster layer stays
    // mounted UNDER the hidden video (hiding the element hides its own poster
    // attribute too) and drops away in the same commit the video is revealed.
    <div className="relative h-dvh w-screen">
      {!videoReady && <div className="absolute inset-0">{videoPoster}</div>}
      <div
        className="absolute inset-x-0 top-0 flex items-center justify-center"
        style={{ height: `calc(100% - ${controlsHeight}px)` }}
      >
        <VideoViewer
          url={attachmentUrl}
          onVideoElement={setVideoEl}
          onTap={toggleVideoControls}
          className={cn(!videoReady && "opacity-0")}
          onReady={() => setVideoReady(true)}
          // The cached thumbnail when we have it; else the ThumbHash data URL,
          // which is available SYNCHRONOUSLY from the message meta — the
          // thumbnail arrives via an async IndexedDB read, so on the video's
          // mount render it is almost always still "" and the element briefly
          // had no poster at all (a black box until the first frame decoded).
          poster={thumbnailUrl || placeholderUrl || undefined}
          onError={(mediaError) => {
            // eslint-disable-next-line no-console
            console.error(
              `[viewer] small-video element error (id=${attachment.id})`,
              mediaError?.code,
              mediaError?.message,
            );
            setVideoElementError(true);
            setVideoElementErrorCode(mediaError?.code ?? null);
          }}
        />
      </div>
    </div>
  );
  const pdf = (
    // The fallback needs its own sized box for the same reason PdfViewer's
    // does: this renders inside TransformComponent, whose CSS transform makes
    // it the containing block for absolute descendants. LoadingScreen is
    // `absolute inset-0 size-full`, so without a sized ancestor its height
    // collapses to zero and the two rings flatten into bars. Matches the video
    // branch above.
    <Suspense
      fallback={
        <div className="relative h-dvh w-screen">
          <LoadingScreen />
        </div>
      }
    >
      <PdfViewer url={attachmentUrl} page={page} setPages={setPageNumber} />
    </Suspense>
  );
  const extraControls = isPdf ? (
    <Suspense fallback={<div>Loading...</div>}>
      <PdfNavigation page={page} pageNumber={pageNumber} setPage={setPage} />
    </Suspense>
  ) : isVideo ? (
    // Custom playback controls in the (non-scaling) bottom bar — the video
    // element itself is bare, so nothing native scales with zoom or collides
    // with iOS's glass overlay. Interactions poke the auto-hide timer.
    <div className="flex items-center gap-5">
      <button
        type="button"
        aria-label={playback.isPlaying ? "Pause" : "Play"}
        onClick={() => {
          playback.togglePlay();
          pokeVideoControls();
        }}
        disabled={!videoEl}
        className="cursor-pointer opacity-90 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        {playback.isPlaying ? (
          <Pause className="size-7" />
        ) : (
          <Play className="size-7" />
        )}
      </button>
      <button
        type="button"
        aria-label={playback.muted ? "Unmute" : "Mute"}
        onClick={() => {
          playback.toggleMute();
          pokeVideoControls();
        }}
        disabled={!videoEl}
        className="cursor-pointer opacity-90 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        {playback.muted ? (
          <VolumeX className="size-7" />
        ) : (
          <Volume2 className="size-7" />
        )}
      </button>
    </div>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && requestClose()}>
      <DialogContent
        aria-label="Media viewer"
        // Backdrop behind the (letterboxed) media at 90% + a frosted blur — the
        // message list is barely visible and blurred through it — black in dark
        // theme, white in light. bg-*/90 + backdrop-blur tint/blur only what's
        // BEHIND this layer; the media and controls (its children) stay fully
        // opaque and crisp. The shared overlay (bg-black/80) is made transparent
        // so it doesn't compound with this backdrop (which would darken/muddy it).
        overlayClassName="bg-transparent"
        closeClassName="top-[max(1rem,env(safe-area-inset-top,0px))]"
        className="flex h-full min-w-full items-center justify-center gap-0 bg-white/90 p-0 backdrop-blur-md dark:bg-black/90"
        onEscapeKeyDown={(e) => {
          // Zoomed: Esc first resets the zoom (standard lightbox), so you don't
          // lose your place by overshooting the close. At 1× it falls through
          // to Radix's default close (→ onOpenChange → requestClose).
          if (isZoomedRef.current) {
            e.preventDefault();
            transformRef.current?.resetTransform();
          }
        }}
      >
        <DialogTitle className="sr-only">
          {filename || "Media viewer"}
        </DialogTitle>
        <DialogDescription className="sr-only" />
        {/* Polite live region announcing slide position to screen readers. */}
        {attachments.length > 1 && (
          <span aria-live="polite" className="sr-only">
            {`${index + 1} of ${attachments.length}`}
          </span>
        )}
        <div
          {...swipeHandlers}
          className="flex h-full w-screen flex-col items-center justify-center"
        >
          {isLargeVideo &&
          previewedMessageExists !== false &&
          (largeVideoError || videoElementError || largeVideoStreamError) ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
              <p>{videoErrorMessage}</p>
            </div>
          ) : smallVideoUndecodable ? (
            // The bytes arrived (attachmentUrl is a local blob) but the
            // element can't DECODE them — a codec this browser doesn't
            // support (e.g. iPhone HEVC on a codec-less engine). Retry would
            // redecode the same bytes, so offer Download instead.
            <UnplayableVideoCard url={attachmentUrl} filename={filename} />
          ) : mediaUnavailable || smallVideoError ? (
            // Nothing fetched at all, or a transient <video> failure (codes
            // 1/2/none — see smallVideoUndecodable): a retry re-fetches and
            // self-heals a dead cached blob, so this card offers one.
            <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
              <AlertCircle className="size-8" />
              <p>This attachment couldn&apos;t be loaded.</p>
              <button
                type="button"
                onClick={() => {
                  // Clear the <video> error latch so the retried element can
                  // re-fetch and re-decode instead of staying on this card.
                  setVideoElementError(false);
                  setVideoElementErrorCode(null);
                  setReloadKey((k) => k + 1);
                }}
                className="rounded-md bg-primary/20 px-4 py-1.5 text-foreground hover:bg-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                Retry
              </button>
            </div>
          ) : (isVideo || isPdf) &&
            open &&
            (loading || attachmentUrl === "") ? (
            // Still fetching + decrypting. Videos show the blurred poster;
            // PDFs show a spinner — WITHOUT this gate react-pdf was handed an
            // empty `file` and rendered its own "No PDF file specified."
            // message for the whole download (~4 s on a first open; instant on
            // later opens because the blob is cached), which reads as a failure.
            // Images don't gate here — ViewerImage handles their placeholder.
            isVideo ? (
              // Poster + the shared download's indicator: the determinate
              // ring while bytes flow; an indeterminate spinner only after a
              // grace period — so pure disk reads of already-downloaded media
              // show the poster alone and never look like a re-download.
              <div className="relative h-dvh w-screen">
                {videoPoster}
                <ViewerDownloadProgress
                  attachmentId={attachment.id ?? ""}
                  bottomInset={controlsHeight}
                />
              </div>
            ) : (
              <LoadingScreen />
            )
          ) : (
            <TransformWrapper
              // No double-tap zoom on VIDEOS: a single tap toggles the
              // immersive controls, and any double-tap gesture would force a
              // ~250ms disambiguation delay on every tap (and video players'
              // double-tap convention is seek-skip, not zoom). Pinch + the
              // toolbar magnifiers cover video zoom. Images keep it.
              doubleClick={{ mode: "toggle", disabled: isVideo }}
              initialPositionX={0}
              initialPositionY={0}
              initialScale={1}
              // Re-sync the zoom flags from a freshly (re)mounted wrapper —
              // react-zoom-pan-pinch doesn't fire onTransformed for its own
              // mount-time transform, and the wrapper remounts on close/open
              // and around the video-buffering branch.
              onInit={(ref) => {
                transformRef.current = ref;
                handleTransformed(ref, ref.state);
              }}
              // Per-type zoom cap (react-zoom-pan-pinch defaults to 8, which
              // pixelates photos and turns lower-res video to mush). Images/PDF
              // stay useful at 4×; video pixelates faster so it's capped at 2×.
              maxScale={isVideo ? 2 : 4}
              // MEDIA ONLY: pan only while zoomed, so at scale 1 the
              // horizontal drag is free for swipe navigation (see
              // swipeHandlers). PDFs keep panning at every scale — a desktop
              // PDF renders at Page scale 2 and always overflows the viewport,
              // and dragging is how you move around the page (docs don't
              // participate in swipe nav anyway).
              panning={{ disabled: (isImage || isVideo) && !isZoomed }}
              onTransformed={handleTransformed}
            >
              <>
                <TransformComponent wrapperClass="relative !w-screen !h-full">
                  {isImage ? (
                    image
                  ) : isVideo ? (
                    video
                  ) : isPdf ? (
                    pdf
                  ) : attachment.fileType ? (
                    // Only a real attachment of an unsupported type shows this.
                    // While the dialog fades out on close the attachment is
                    // reset to {} (no fileType) — render nothing then, so this
                    // fallback doesn't flash during the close animation.
                    <div>Content not supported</div>
                  ) : null}
                </TransformComponent>
                <ViewerControls
                  ref={controlsRef}
                  url={attachmentUrl}
                  filename={filename}
                  extraControls={extraControls}
                  hidden={isVideo && !videoControlsVisible}
                />
                {isVideo && (
                  // Progress strip: thin passive line while immersed, docks on
                  // top of the bar as a seek slider when the controls are up.
                  // Sibling of the transform layer — never scales with zoom.
                  // Keyed per item so an in-flight drag's state can never
                  // leak across a swipe to the next video.
                  <VideoScrubBar
                    key={attachment.id}
                    currentTime={playback.currentTime}
                    duration={playback.duration}
                    expanded={videoControlsVisible}
                    bottomInset={controlsHeight}
                    onSeek={playback.seek}
                    onInteract={pokeVideoControls}
                  />
                )}
              </>
            </TransformWrapper>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AttachmentViewerDialog;
